import { FILTER_TYPE, PER_PAGE } from "@/constant/constants";
import React, { useCallback, useMemo, useRef } from "react";

import { CONFIRMATION_MODAL_TYPES } from "@/Modals/NewConfirmationModal/constants";
import BaseImportModal from "@/Modals/WebDataManagement/MasterData/BaseImport/BaseImportModal";
import { useGet } from "@/apiHooks/HttpFunctions";
import DataTable from "@/components/NewTable";
import { statusFilters } from "@/constant/statusConstants";
import { useModalConfirmation } from "@/context/ModalConfirmationContext";
import useUrlParams from "@/hooks/useUrlParams";
import Actions from "./Actions";
import AdminAdjustmentActionModal from "@/Modals/AdminAdjustmentAction";

export default function BaseTable({
  label,
  apiURL,
  columns = [],
  enableSortData = false,
  ModalComponent,
  importData,
  enableStatus = true,
  enableFeatured = true,
  enableExport = true,
  enableAdd = true,
  enableEdit = true,
  enableDelete = true,
  costumActions = null,
  additionalFilters = [],
}) {
  // Hooks
  const params = useUrlParams();
  const { openModalConfirmation } = useModalConfirmation();

  // Refs
  const modalRef = useRef(null);
  const modalImportRef = useRef(null);

  // Derived States
  const filterData = useMemo(() => {
    const statusFiltersMap = statusFilters();
    return [
      enableStatus && {
        id: "filter[status]",
        label: "Status",
        type: FILTER_TYPE.SELECT,
        options: Object.entries(statusFiltersMap).map(([value, label]) => ({
          label,
          value,
        })),
      },
      ...additionalFilters,
    ].filter(Boolean);
  }, [enableStatus, additionalFilters]);

  // Callbacks
  const handleModalAction = useCallback(
    (type, data) => {
      openModalConfirmation({
        type,
        title: data?.name,
        id: data?.id,
        apiURL,
      });
    },
    [openModalConfirmation, apiURL]
  );

  const handleOnClickAdd = useCallback(() => modalRef.current?.open(), []);
  const handleOnClickImport = useCallback(
    () => modalImportRef.current?.open(),
    []
  );
  const handleOnClickEdit = useCallback(
    (data) => modalRef.current?.open({ id: data.id }),
    []
  );

  // Data Fetching
  const { data, isFetching, error, refetch } = useGet({
    apiURL,
    params: {
      ...params,
      perPage: params.perPage || PER_PAGE,
    },
  });

  //Admin Adjustment Action Modal
  const initialModalState = useMemo(() => ({isOpen: false, header: '', endpointUrl: '',}), []);
  let [modalState, setModalState] = React.useState(initialModalState);

  const handleDelete = useCallback((row) => {
    setModalState({
      isOpen: true,
      header: 'Delete Photo',
      endpointUrl: `${apiURL}/delete/${row.id}`,
    });
  }, [apiURL]);
  const handleUpdateFeatured = useCallback((row) => {
    setModalState({
      isOpen: true,
      header: row.is_featured ? 'Remove Featured' : 'Set as Featured Photo',
      endpointUrl: `${apiURL}/change-feature-status/${row.id}`,
    });
  }, [apiURL]);
  const onAdjustmentSuccess = useCallback(() => {
    setModalState(initialModalState);
  }, [initialModalState]);
  const onAdjustmentCancel = useCallback(() => {
    setModalState(initialModalState);
  }, [initialModalState]);

  // Merged Columns
  const mergedColumns = useMemo(() => {
    const baseProps = {
      accessorKey: "actions",
      header: "Actions",
      enableResizing: false,
      enableSorting: false,
      enablePinning: true,
    };
    const actionsColumn = costumActions
      ? {
          ...baseProps,
          cell: ({ row }) => costumActions({ row }),
        }
      : {
          ...baseProps,
          cell: ({ row }) => (
            <Actions
              row={row}
              handleOnClickDelete={handleDelete}
              handleOnChangeStatus={
                enableStatus &&
                ((data) =>
                  handleModalAction(
                    CONFIRMATION_MODAL_TYPES.CHANGE_STATUS,
                    data
                  ))
              }
              handleOnChangeFeature={handleUpdateFeatured}
              handleOnClickEdit={enableEdit && handleOnClickEdit}
              enableStatus={enableStatus}
              enableFeatured={enableFeatured}
            />
          ),
        };

    return [actionsColumn, ...columns];
  }, [costumActions, columns, handleDelete, enableStatus, handleUpdateFeatured, enableEdit, handleOnClickEdit, enableFeatured, handleModalAction]);

  // Render
  if (error) {
    return (
      <div>
        <p>Error loading data...</p>
        <button onClick={refetch}>Retry</button>
      </div>
    );
  }

  return (
    <>
      <DataTable
        data={data}
        columns={mergedColumns}
        isLoading={isFetching}
        searchKey="filter[keyword]"
        exportData={
          enableExport && {
            api: apiURL,
            label: `${label} List`,
          }
        }
        importData={
          importData && {
            onClick: handleOnClickImport,
          }
        }
        addData={
          enableAdd && {
            onClick: handleOnClickAdd,
          }
        }
        sortData={
          enableSortData && {
            api: apiURL,
          }
        }
        filterData={filterData}
      />
      {ModalComponent && <ModalComponent ref={modalRef} />}
      <AdminAdjustmentActionModal
        isOpen={modalState.isOpen}
        headerText={modalState.header}
        endpointUrl={modalState.endpointUrl}
        onSuccess={onAdjustmentSuccess}
        onCancel={onAdjustmentCancel}
      />
      {importData && (
        <BaseImportModal
          ref={modalImportRef}
          importData={importData}
          apiURL={apiURL}
        />
      )}
    </>
  );
}
